'use client';

import type {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  CSSProperties,
  MouseEvent as ReactMouseEvent,
  ReactElement,
  ReactNode,
} from 'react';
import { cloneElement, isValidElement, useMemo } from 'react';
import { cx } from '../utils/cx';
import { Link } from './link';

import type { IconName, ResolvedIcon } from '../../packages/icons/icon-registry';
import { IconRegistry } from '../../packages/icons/icon-registry';

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'secondaryOutline'
  | 'outline'
  | 'ghost'
  | 'danger'
  | 'danger-ghost'
  | 'premium'
  | 'menu'
  | 'danger-menu'
  | 'text'
  | 'tonal'
  | 'filled'
  | 'bottomNav'
  | 'floating'
  | 'entryPoint';

export type ButtonSize = 'icon-sm' | 'sm' | 'md' | 'lg';

export type ButtonPreset = 'default' | 'navRailNew';
export type ButtonNavLayout = 'icon' | 'horizontal';
export type ButtonEventPayload = Record<string, unknown> | string | number | boolean | null;

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  block?: boolean;
  icon?: IconName | string | null;
  iconPosition?: 'left' | 'right';
  iconSize?: number;
  iconOnly?: boolean;
  label?: string;
  children?: ReactNode;
  text?: string;
  loading?: boolean;
  loadingText?: ReactNode;
  disabled?: boolean;
  unstyled?: boolean;
  bg?: string;
  color?: string;
  radius?: string | number;
  fontSize?: number;
  fontWeight?: string | number;
  href?: string;
  hrefTemplate?: string;
  prefetch?: boolean;
  target?: AnchorHTMLAttributes<HTMLAnchorElement>['target'];
  rel?: AnchorHTMLAttributes<HTMLAnchorElement>['rel'];
  preset?: ButtonPreset;
  navLayout?: ButtonNavLayout;
  collapseTextOnMobile?: boolean;
  validateForm?: boolean;
  asChild?: boolean;
  active?: boolean;
  eventPayload?: ButtonEventPayload;
}

const URL_SCHEME_PATTERN = /^[a-zA-Z][a-zA-Z\d+\-.]*:/;
const LOCALE_PREFIXED_HREF = /^\/([a-z]{2}(?:-[A-Za-z]{2})?)(\/|$)/i;

function hasUrlScheme(value: string): boolean {
  return value.startsWith('//') || URL_SCHEME_PATTERN.test(value);
}

/**
 * External links are always external: peel stacked locale prefixes
 * ("/en/https://doi.org/...", "/en/en/https://...") off an absolute URL and
 * return the bare URL. The Link atom classifies the result as external on
 * BOTH the server and the client, so both renders emit the same plain
 * external <a> (href + target + rel) — no hydration mismatch, and NextLink
 * never sees (or re-prefixes) an absolute URL.
 */
function unwrapLocalizedAbsoluteHref(href: string): string {
  const trimmed = href.trim();
  if (!trimmed) return trimmed;

  let current = trimmed;
  for (let depth = 0; depth < 8; depth += 1) {
    if (hasUrlScheme(current)) return current;
    if (current.startsWith('/') && hasUrlScheme(current.slice(1))) {
      return current.slice(1);
    }

    const match = LOCALE_PREFIXED_HREF.exec(current);
    if (!match) break;
    current = current.slice((match[1] as string).length + 1);
  }

  if (hasUrlScheme(current)) return current;
  if (current.startsWith('/') && hasUrlScheme(current.slice(1))) {
    return current.slice(1);
  }
  return trimmed;
}

function coerceBoolean(value: unknown, defaultValue: boolean): boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true') return true;
    if (normalized === 'false' || normalized === '') return false;
  }
  if (typeof value === 'number') return value !== 0;
  return defaultValue;
}

function addStableButtonEventData(
  event: ReactMouseEvent<HTMLButtonElement>,
  eventPayload: ButtonEventPayload | undefined,
): void {
  const eventRecord = event as unknown as Record<string, unknown>;
  const payloadRecord =
    eventPayload !== null && typeof eventPayload === 'object' ? eventPayload : null;
  const buttonValue = event.currentTarget.value;

  let normalizedValue: unknown = buttonValue;
  if (payloadRecord?.value !== undefined) {
    normalizedValue = payloadRecord.value;
  } else if (eventPayload !== undefined && eventPayload !== null && !payloadRecord) {
    normalizedValue = eventPayload;
  }

  try {
    // React clears currentTarget after the synchronous click handler. Copy the
    // declarative payload and native value onto the event so routed trigger
    // adapters can safely process or wrap it later.
    if (payloadRecord) {
      for (const [key, value] of Object.entries(payloadRecord)) {
        if (eventRecord[key] === undefined) eventRecord[key] = value;
      }
    }

    if (eventPayload !== undefined) {
      eventRecord.eventPayload = eventPayload;
      eventRecord.payload = eventPayload;
    }

    if (eventRecord.value === undefined) eventRecord.value = normalizedValue;
  } catch {
    // Preserve the native click behavior if a future event implementation is
    // not extensible. The original handler still receives the React event.
  }
}

function sizeClass(size: ButtonSize): string {
  switch (size) {
    case 'icon-sm':
      return 'h-10 w-10 px-0';
    case 'sm':
      return 'h-10 px-4 text-sm';
    case 'lg':
      return 'h-12 px-5 py-1 text-lg';
    case 'md':
    default:
      return 'h-11 px-5 text-sm';
  }
}

function variantClass(variant: ButtonVariant): string {
  switch (variant) {
    case 'secondary':
      return 'bg-secondary text-secondary shadow-xs hover:bg-secondary_hover';
    case 'secondaryOutline':
    case 'outline':
      return 'border border-secondary_alt bg-transparent text-secondary hover:bg-primary_hover focus-visible:border-brand';
    case 'ghost':
      return 'border border-transparent bg-transparent text-secondary hover:bg-primary_hover';
    case 'danger':
      return 'bg-danger text-on-danger shadow-xs hover:bg-danger_hover focus-visible:ring-danger/20';
    case 'danger-ghost':
      return 'border border-transparent bg-transparent text-error hover:bg-error-secondary';
    case 'premium':
      return 'bg-gradient-to-r from-brand via-brand to-brand-secondary text-on-brand shadow-sm shadow-brand/15 hover:opacity-95';
    case 'menu':
      return 'border border-transparent bg-transparent text-secondary hover:bg-primary_hover';
    case 'danger-menu':
      return 'border border-transparent bg-transparent text-error hover:bg-error-secondary';
    case 'text':
      return 'border border-transparent bg-transparent text-secondary hover:bg-primary_hover';
    case 'tonal':
      return 'bg-brand-primary text-brand shadow-xs hover:bg-brand-primary/80';
    case 'filled':
      return 'bg-secondary text-secondary shadow-xs hover:bg-secondary_hover';
    case 'bottomNav':
      return 'flex-1 rounded-none text-secondary hover:text-brand data-[active=true]:text-brand';
    case 'floating':
      return '!h-12 !w-12 !px-0 !rounded-full border border-white/60 bg-gradient-to-br from-brand via-brand to-brand-secondary text-on-brand shadow-lg shadow-brand/20 ring-1 ring-inset ring-white/35 transition-transform hover:scale-[1.02] hover:shadow-xl hover:shadow-brand/25';
    case 'entryPoint':
      // entryPoint buttons are fully styled via `className`; no variant base.
      return '';
    case 'primary':
    default:
      return 'bg-brand text-on-brand shadow-sm shadow-brand/10 hover:bg-brand/95 focus-visible:ring-brand/20';
  }
}

function isMenuVariant(variant: ButtonVariant): boolean {
  return variant === 'menu' || variant === 'danger-menu';
}

function resolveFormNoValidate(options: {
  formNoValidate: ButtonHTMLAttributes<HTMLButtonElement>['formNoValidate'];
  validateForm: boolean | undefined;
}): ButtonHTMLAttributes<HTMLButtonElement>['formNoValidate'] {
  const { formNoValidate, validateForm } = options;
  if (formNoValidate !== undefined) return formNoValidate;
  if (validateForm === false) return true;
  return undefined;
}

function isCssColorValue(value: string): boolean {
  const v = value.trim();
  if (!v) return false;
  return (
    v.startsWith('#') ||
    v.startsWith('rgb(') ||
    v.startsWith('rgba(') ||
    v.startsWith('hsl(') ||
    v.startsWith('hsla(') ||
    v.startsWith('var(')
  );
}

function buildStyleOverrides(opts: {
  bg: string | undefined;
  color: string | undefined;
  radius: string | number | undefined;
  fontSize: number | undefined;
  fontWeight: string | number | undefined;
}): CSSProperties {
  const style: CSSProperties = {};

  if (typeof opts.bg === 'string' && isCssColorValue(opts.bg)) style.backgroundColor = opts.bg.trim();
  if (typeof opts.color === 'string' && isCssColorValue(opts.color)) style.color = opts.color.trim();

  if (typeof opts.radius === 'number') style.borderRadius = `${opts.radius}px`;
  if (typeof opts.radius === 'string' && opts.radius.trim()) style.borderRadius = opts.radius.trim();

  if (typeof opts.fontSize === 'number' && Number.isFinite(opts.fontSize)) style.fontSize = `${opts.fontSize}px`;
  if (typeof opts.fontWeight === 'number' || typeof opts.fontWeight === 'string') {
    style.fontWeight = opts.fontWeight as CSSProperties['fontWeight'];
  }

  return style;
}

function Spinner({ className }: { className?: string }) {
  return (
    <svg className={cx('animate-spin', className)} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
      <path
        className="opacity-75"
        d="M22 12a10 10 0 0 1-10 10"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  );
}

function renderIcon(icon: IconName | string | null | undefined, iconSize: number): ReactNode {
  if (!icon) return null;

  const IconComponent: ResolvedIcon | null = IconRegistry.get(String(icon));
  if (!IconComponent) return null;

  return <IconComponent size={iconSize} strokeWidth={2} aria-hidden="true" className="shrink-0" />;
}

export function Button({
  variant = 'primary',
  size = 'md',
  fullWidth: fullWidthProp,
  block,
  loading = false,
  loadingText,
  asChild = false,
  unstyled = false,
  icon,
  iconPosition = 'left',
  iconSize = 16,
  iconOnly = false,
  label,
  className,
  disabled = false,
  type,
  children,
  text,
  href,
  hrefTemplate,
  prefetch,
  target,
  rel,
  validateForm,
  preset = 'default',
  navLayout = 'icon',
  collapseTextOnMobile = false,
  bg,
  color,
  radius,
  fontSize,
  fontWeight,
  style,
  active = false,
  eventPayload,
  ...rest
}: ButtonProps) {
  const restWithOnPress = rest as ButtonHTMLAttributes<HTMLButtonElement> & {
    onPress?: unknown;
  };
  const { onPress: _onPress, ...safeRest } = restWithOnPress;

  const fullWidthBool = coerceBoolean(fullWidthProp ?? block, false);
  const loadingBool = coerceBoolean(loading, false);
  const asChildBool = coerceBoolean(asChild, false);
  const unstyledBool = coerceBoolean(unstyled, false);
  const disabledBool = coerceBoolean(disabled, false);
  const iconOnlyBool = coerceBoolean(iconOnly, false);
  const activeBool = coerceBoolean(active, false);

  const isEntryPoint = variant === 'entryPoint';
  // entryPoint buttons own their inner markup. Render `children`/`text` (and
  // any `icon` prop) directly so flex layouts defined in the className keep
  // those nodes as direct children of the button instead of wrapping them in
  // a single <span class="min-w-0"> that collapses the row.
  const rendersRawChildren = isEntryPoint;

  const isDisabled = disabledBool || loadingBool;
  // Absolute external URLs must never carry a locale/host prefix. Unwrap any
  // stacked "/en/" prefixes here so the Link atom sees the bare URL on both
  // renders and always emits the external anchor branch.
  const resolvedHref = unwrapLocalizedAbsoluteHref(String(href ?? hrefTemplate ?? ''));
  const wantsLink = Boolean(resolvedHref) && !asChildBool;

  const baseLabel: ReactNode = children ?? text ?? null;
  const displayLabel: ReactNode = loadingBool && loadingText != null ? loadingText : baseLabel;

  const labelText = String(label ?? '').trim();
  const ariaLabel =
    iconOnlyBool || preset === 'navRailNew'
      ? labelText || (typeof displayLabel === 'string' ? displayLabel : '')
      : labelText;

  const iconEl = renderIcon(icon, iconSize);

  const styleOverrides = useMemo(
    () => buildStyleOverrides({ bg, color, radius, fontSize, fontWeight }),
    [bg, color, fontSize, fontWeight, radius],
  );
  const mergedStyle: CSSProperties | undefined =
    Object.keys(styleOverrides).length > 0 ? { ...(style ?? {}), ...styleOverrides } : style;

  const formNoValidate = resolveFormNoValidate({ formNoValidate: safeRest.formNoValidate, validateForm });
  const buttonRest = safeRest as ButtonHTMLAttributes<HTMLButtonElement>;
  const originalOnClick = buttonRest.onClick;
  const shouldNormalizeClick = eventPayload !== undefined || buttonRest.value !== undefined;
  const eventAwareButtonRest: ButtonHTMLAttributes<HTMLButtonElement> =
    !originalOnClick || !shouldNormalizeClick
      ? buttonRest
      : {
          ...buttonRest,
          onClick: (event: ReactMouseEvent<HTMLButtonElement>) => {
            addStableButtonEventData(event, eventPayload);
            originalOnClick(event);
          },
        };

  if (preset === 'navRailNew') {
    const isHorizontal = navLayout === 'horizontal';
    const shellClassName = cx(
      'group relative inline-flex items-center justify-center overflow-hidden',
      'border border-secondary_alt bg-secondary text-secondary shadow-xs transition-all duration-200',
      'hover:bg-secondary_hover focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/20 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent',
      isDisabled && 'pointer-events-none opacity-60',
      isHorizontal
        ? collapseTextOnMobile
          ? 'h-10 w-10 px-0 sm:w-auto sm:px-3'
          : 'h-10 px-3'
        : 'h-10 w-10 px-0',
      className,
    );

    const content = (
      <>
        {loadingBool ? <Spinner className="h-4 w-4" /> : null}
        {iconEl && iconPosition === 'left' && !loadingBool ? iconEl : null}
        {isHorizontal && !iconOnlyBool ? (
          <span className={cx('min-w-0 truncate', collapseTextOnMobile && 'hidden sm:inline')}>
            {displayLabel}
          </span>
        ) : null}
        {iconEl && iconPosition === 'right' && !loadingBool ? iconEl : null}
      </>
    );

    if (!wantsLink || isDisabled) {
      return (
        <button
          {...eventAwareButtonRest}
          type={type ?? 'button'}
          disabled={isDisabled}
          {...(formNoValidate !== undefined ? { formNoValidate } : {})}
          aria-busy={loadingBool || undefined}
          aria-disabled={isDisabled || undefined}
          aria-current={activeBool ? 'page' : undefined}
          data-active={activeBool || undefined}
          aria-label={ariaLabel || undefined}
          className={shellClassName}
          style={mergedStyle}
        >
          {content}
        </button>
      );
    }

    return (
      <Link
        {...(safeRest as any)}
        href={resolvedHref}
        {...(prefetch !== undefined ? { prefetch } : {})}
        {...(target !== undefined ? { target } : {})}
        {...(rel !== undefined ? { rel } : {})}
        unstyled
        aria-label={ariaLabel || undefined}
        aria-current={activeBool ? 'page' : undefined}
        data-active={activeBool || undefined}
        className={shellClassName}
        style={mergedStyle}
      >
        {content}
      </Link>
    );
  }

  const computedBase = unstyledBool || isEntryPoint
    ? 'disabled:cursor-not-allowed disabled:opacity-60 disabled:shadow-none'
    : (() => {
        if (isMenuVariant(variant)) {
          const base =
            'flex items-center justify-start gap-3 whitespace-nowrap rounded-lg px-3 py-2 text-sm font-medium transition-colors duration-150 ' +
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent';
          const ring = variant === 'danger-menu' ? 'focus-visible:ring-danger' : 'focus-visible:ring-brand';
          const width = fullWidthBool ? 'w-full' : '';
          const disabledClass = isDisabled ? 'cursor-not-allowed opacity-50' : '';
          return cx(base, ring, width, disabledClass, variantClass(variant));
        }

        if (variant === 'bottomNav') {
          return cx(
            'flex flex-col items-center justify-center gap-0.5 p-2 transition-colors duration-200',
            fullWidthBool ? 'w-full' : '',
            isDisabled ? 'cursor-not-allowed opacity-60' : '',
            variantClass(variant),
          );
        }

        const base =
          'inline-flex items-center justify-center gap-2 rounded-xl font-medium transition-all duration-200 active:scale-[0.99] ' +
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent disabled:shadow-none';
        const width = fullWidthBool ? 'w-full' : '';
        const disabledClass = isDisabled ? 'cursor-not-allowed opacity-60' : '';
        return cx(base, width, sizeClass(size), variantClass(variant), disabledClass);
      })();

  const composedClassName = cx(computedBase, className);

  const content = rendersRawChildren ? (
    <>
      {loadingBool ? <Spinner className="h-4 w-4" /> : null}
      {iconEl && iconPosition === 'left' && !loadingBool ? iconEl : null}
      {displayLabel}
      {iconEl && iconPosition === 'right' && !loadingBool ? iconEl : null}
    </>
  ) : (
    <>
      {loadingBool ? <Spinner className="h-4 w-4" /> : null}
      {iconEl && variant !== 'bottomNav' && iconPosition === 'left' && !loadingBool ? iconEl : null}
      {variant === 'bottomNav' && iconEl && !loadingBool ? iconEl : null}

      {iconOnlyBool ? (
        <span className="sr-only">{ariaLabel || 'Action'}</span>
      ) : (
        <span
          className={cx(
            'min-w-0',
            variant === 'bottomNav' ? 'text-[10px] font-medium leading-none' : '',
            loadingBool && 'opacity-90',
          )}
        >
          {displayLabel}
        </span>
      )}

      {iconEl && variant !== 'bottomNav' && iconPosition === 'right' && !loadingBool ? iconEl : null}
    </>
  );

  if (wantsLink) {
    if (isDisabled) {
      return (
        <span
          {...(safeRest as any)}
          aria-disabled="true"
          aria-busy={loadingBool || undefined}
          aria-current={activeBool ? 'page' : undefined}
          data-active={activeBool || undefined}
          aria-label={ariaLabel || undefined}
          tabIndex={-1}
          className={composedClassName}
          style={mergedStyle}
        >
          {content}
        </span>
      );
    }

    return (
      <Link
        {...(safeRest as any)}
        href={resolvedHref}
        {...(prefetch !== undefined ? { prefetch } : {})}
        {...(target !== undefined ? { target } : {})}
        {...(rel !== undefined ? { rel } : {})}
        unstyled
        className={composedClassName}
        aria-busy={loadingBool || undefined}
        aria-current={activeBool ? 'page' : undefined}
        data-active={activeBool || undefined}
        aria-label={ariaLabel || undefined}
        style={mergedStyle}
      >
        {content}
      </Link>
    );
  }

  if (asChildBool) {
    const child = children as ReactElement | null;
    if (!isValidElement(child)) {
      return (
        <button
          {...eventAwareButtonRest}
          type={type ?? (variant === 'primary' ? 'submit' : 'button')}
          disabled={isDisabled}
          {...(formNoValidate !== undefined ? { formNoValidate } : {})}
          aria-busy={loadingBool || undefined}
          aria-disabled={isDisabled || undefined}
          aria-current={activeBool ? 'page' : undefined}
          data-active={activeBool || undefined}
          aria-label={ariaLabel || undefined}
          className={composedClassName}
          style={mergedStyle}
        >
          {content}
        </button>
      );
    }

    const childProps: Record<string, unknown> = {
      className: cx((child.props as any)?.className, composedClassName),
      'aria-disabled': isDisabled || undefined,
      'aria-busy': loadingBool || undefined,
      'aria-current': activeBool ? 'page' : (child.props as any)?.['aria-current'],
      'data-active': activeBool || (child.props as any)?.['data-active'],
      'aria-label': ariaLabel || (child.props as any)?.['aria-label'],
      tabIndex: isDisabled ? -1 : (child.props as any)?.tabIndex,
      style: { ...((child.props as any)?.style ?? {}), ...(mergedStyle ?? {}) },
      onClick: isDisabled
        ? (e: unknown) => {
            (e as any)?.preventDefault?.();
            (e as any)?.stopPropagation?.();
          }
        : (child.props as any)?.onClick,
    };

    return cloneElement(child, childProps);
  }

  return (
    <button
      {...eventAwareButtonRest}
      type={type ?? (variant === 'primary' ? 'submit' : 'button')}
      disabled={isDisabled}
      {...(formNoValidate !== undefined ? { formNoValidate } : {})}
      aria-busy={loadingBool || undefined}
      aria-disabled={isDisabled || undefined}
      aria-current={activeBool ? 'page' : undefined}
      data-active={activeBool || undefined}
      aria-label={ariaLabel || undefined}
      className={composedClassName}
      style={mergedStyle}
    >
      {content}
    </button>
  );
}

Button.displayName = 'Button';